import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const hasRunRef = useRef(false);

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState(
    "Verifying your email..."
  );

  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const run = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Missing verification token.");
        return;
      }

      try {
        const res = await axios.post(
          "/api/users/verify-email",
          { token }
        );

        setStatus("success");
        setMessage(
          res.data?.message ||
            "Your email has been verified."
        );
      } catch (err) {
        setStatus("error");
        setMessage(
          err.response?.data?.error ||
            "Failed to verify email."
        );
      }
    };

    run();
  }, [token]);

  const isLoading = status === "loading";
  const isSuccess = status === "success";
  const isError = status === "error";

  return (
    <div
      className="
        relative min-h-screen overflow-hidden
        bg-[rgb(var(--bg-app))]
        text-[rgb(var(--text-main))]
      "
    >
      {/* Veacon accent */}
      <div
        className="
          h-1.5 w-full
          bg-[linear-gradient(90deg,rgb(var(--brand)),rgb(var(--cool)),rgb(var(--intent)))]
        "
      />

      {/* Background glow */}
      <div
        className="
          pointer-events-none absolute
          left-1/2 top-[-260px]
          h-[560px] w-[560px]
          -translate-x-1/2
          rounded-full blur-3xl
        "
        style={{
          background:
            "radial-gradient(circle, rgb(var(--brand) / 0.10), transparent 68%)",
        }}
      />

      <main
        className="
          relative flex min-h-[calc(100vh-6px)]
          items-center justify-center
          px-4 py-10
          sm:px-6
        "
      >
        <div className="w-full max-w-md">
          {/* Veacon brand */}
          <Link
            to="/"
            aria-label="Veacon home"
            className="
              mx-auto mb-8
              flex w-fit items-center gap-3
            "
          >
            <div
              className="
                flex h-12 w-12 items-center justify-center
                overflow-hidden rounded-2xl
                border border-[rgb(var(--border))]
                bg-[rgb(var(--surface))]
                shadow-[var(--shadow-soft)]
              "
            >
              <img
                src="/logo.svg"
                alt=""
                className="h-7 w-7"
              />
            </div>

            <div>
              <div
                className="
                  text-base font-extrabold
                  tracking-tight
                  text-[rgb(var(--text-main))]
                "
              >
                Veacon
              </div>

              <div
                className="
                  text-xs font-semibold
                  text-[rgb(var(--text-muted))]
                "
              >
                Find things to do.
              </div>
            </div>
          </Link>

          {/* Verification card */}
          <section
            className="
              rounded-[2rem]
              border border-[rgb(var(--border))]
              bg-[rgb(var(--surface))]
              p-6
              text-center
              shadow-[var(--shadow-soft)]
              sm:p-8
            "
          >
            {/* Loading */}
            {isLoading ? (
              <>
                <div
                  className="
                    mx-auto flex h-14 w-14
                    items-center justify-center
                    rounded-2xl
                    bg-[rgb(var(--cool)/0.10)]
                  "
                >
                  <div
                    className="
                      h-5 w-5
                      animate-spin rounded-full
                      border-2
                      border-[rgb(var(--border))]
                      border-t-[rgb(var(--cool))]
                    "
                    aria-hidden="true"
                  />
                </div>

                <div
                  className="
                    mt-5 text-xs font-extrabold
                    uppercase tracking-[0.16em]
                    text-[rgb(var(--cool))]
                  "
                >
                  Just a moment
                </div>

                <h1
                  className="
                    mt-2
                    text-3xl font-extrabold
                    tracking-[-0.04em]
                    text-[rgb(var(--text-main))]
                  "
                >
                  Verifying your email
                </h1>

                <p
                  className="
                    mx-auto mt-3 max-w-sm
                    text-sm leading-6
                    text-[rgb(var(--text-muted))]
                  "
                >
                  {message}
                </p>
              </>
            ) : null}

            {/* Success */}
            {isSuccess ? (
              <>
                <div
                  className="
                    mx-auto flex h-14 w-14
                    items-center justify-center
                    rounded-2xl
                    bg-[rgb(var(--brand)/0.10)]
                    text-xl font-black
                    text-[rgb(var(--brand))]
                  "
                  aria-hidden="true"
                >
                  ✓
                </div>

                <div
                  className="
                    mt-5 text-xs font-extrabold
                    uppercase tracking-[0.16em]
                    text-[rgb(var(--brand))]
                  "
                >
                  Verified
                </div>

                <h1
                  className="
                    mt-2
                    text-3xl font-extrabold
                    tracking-[-0.04em]
                    text-[rgb(var(--text-main))]
                  "
                >
                  Email verified
                </h1>

                <p
                  className="
                    mx-auto mt-3 max-w-sm
                    text-sm leading-6
                    text-[rgb(var(--text-muted))]
                  "
                >
                  {message}
                </p>

                <div className="mt-7 space-y-3">
                  <a
                    href="veacon://"
                    className="
                      inline-flex w-full
                      items-center justify-center
                      rounded-2xl
                      border border-[rgb(var(--brand)/0.35)]
                      bg-[rgb(var(--brand))]
                      px-5 py-3.5
                      text-sm font-extrabold text-white
                      shadow-[var(--shadow-soft)]
                      transition
                      hover:-translate-y-0.5
                      hover:opacity-90
                    "
                  >
                    Open Veacon
                  </a>

                  <Link
                    to="/"
                    className="
                      inline-flex w-full
                      items-center justify-center
                      rounded-2xl
                      border border-[rgb(var(--border))]
                      bg-[rgb(var(--bg-app))]
                      px-5 py-3.5
                      text-sm font-extrabold
                      text-[rgb(var(--text-main))]
                      transition
                      hover:bg-[rgb(var(--elevated))]
                    "
                  >
                    Back to home
                  </Link>
                </div>
              </>
            ) : null}

            {/* Error */}
            {isError ? (
              <>
                <div
                  className="
                    mx-auto flex h-14 w-14
                    items-center justify-center
                    rounded-2xl
                    bg-[rgb(var(--danger)/0.09)]
                    text-xl font-black
                    text-[rgb(var(--danger))]
                  "
                  aria-hidden="true"
                >
                  !
                </div>

                <div
                  className="
                    mt-5 text-xs font-extrabold
                    uppercase tracking-[0.16em]
                    text-[rgb(var(--danger))]
                  "
                >
                  Verification issue
                </div>

                <h1
                  className="
                    mt-2
                    text-3xl font-extrabold
                    tracking-[-0.04em]
                    text-[rgb(var(--text-main))]
                  "
                >
                  We couldn&apos;t verify your email
                </h1>

                <p
                  className="
                    mx-auto mt-3 max-w-sm
                    text-sm leading-6
                    text-[rgb(var(--text-muted))]
                  "
                >
                  {message}
                </p>

                <div className="mt-7 space-y-3">
                  <Link
                    to="/verify-email-pending"
                    className="
                      inline-flex w-full
                      items-center justify-center
                      rounded-2xl
                      border border-[rgb(var(--brand)/0.35)]
                      bg-[rgb(var(--brand))]
                      px-5 py-3.5
                      text-sm font-extrabold text-white
                      shadow-[var(--shadow-soft)]
                      transition
                      hover:-translate-y-0.5
                      hover:opacity-90
                    "
                  >
                    Verification help
                  </Link>

                  <Link
                    to="/"
                    className="
                      inline-flex w-full
                      items-center justify-center
                      rounded-2xl
                      border border-[rgb(var(--border))]
                      bg-[rgb(var(--bg-app))]
                      px-5 py-3.5
                      text-sm font-extrabold
                      text-[rgb(var(--text-main))]
                      transition
                      hover:bg-[rgb(var(--elevated))]
                    "
                  >
                    Back to Veacon
                  </Link>
                </div>
              </>
            ) : null}
          </section>

          <div
            className="
              mt-6 text-center
              text-xs
              text-[rgb(var(--text-muted))]
            "
          >
            Having trouble?{" "}
            <Link
              to="/contact"
              className="
                font-bold
                text-[rgb(var(--text-main))]
                transition
                hover:text-[rgb(var(--brand))]
              "
            >
              Contact support
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}